import type { AiSearch } from '@/store/slices/aiAnalysisTypes';
import type { UniversalSearchState } from '@/components/conference/UniversalSearch/types';

/** Supplied only by the call-bound loader, from its actual calls row/query. */
export interface SearchSnapshotEnvelope {
  roomCallId: string;
  dbCallId?: string | null;
  roomName?: string | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === 'object' && !Array.isArray(value));
}

function aliases(envelope: unknown): string[] {
  if (!isRecord(envelope) || typeof envelope.roomCallId !== 'string' || !envelope.roomCallId.trim())
    return [];
  if (
    envelope.dbCallId != null &&
    (typeof envelope.dbCallId !== 'string' ||
      !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(envelope.dbCallId))
  )
    return [];
  if (
    envelope.roomName != null &&
    (typeof envelope.roomName !== 'string' || !envelope.roomName.trim())
  )
    return [];
  return [envelope.roomCallId, envelope.dbCallId, envelope.roomName].filter(
    (value): value is string => typeof value === 'string',
  );
}

/** Add provenance without altering a query, result, snapshot or existing originating tag. */
export function bindSearchSnapshots(
  searches: AiSearch[] | undefined,
  envelope: SearchSnapshotEnvelope,
): AiSearch[] | undefined {
  if (!Array.isArray(searches) || aliases(envelope).length === 0) return searches;
  return searches.map((search) => {
    if (!isRecord(search) || (search.filters != null && !isRecord(search.filters))) return search;
    const filters = search.filters || {};
    return {
      ...search,
      filters: {
        ...filters,
        // An explicit foreign, false or malformed tag is never relabelled.
        ...(!Object.prototype.hasOwnProperty.call(filters, 'callId')
          ? { callId: envelope.roomCallId }
          : {}),
        // This projection is recomputed from the verified materialization,
        // independently of the stored originating tag above.
        searchSnapshotEnvelope: { ...envelope },
      },
    };
  });
}

/** Unknown history is retained, but cannot be attributed to this panel. */
export function searchSnapshotForCall(
  searches: AiSearch[] | undefined,
  callId: string,
): UniversalSearchState | null {
  if (!callId || !Array.isArray(searches)) return null;
  for (let i = searches.length - 1; i >= 0; i--) {
    const filters = searches[i]?.filters;
    if (!isRecord(filters) || typeof filters.callId !== 'string') continue;
    const proven = aliases(filters.searchSnapshotEnvelope);
    if (!proven.includes(callId) || !proven.includes(filters.callId)) continue;
    if (isRecord(filters.snapshot)) return filters.snapshot as unknown as UniversalSearchState;
  }
  return null;
}
